import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './AdminDashboard.module.css';
import { Button } from '../../components/common/Button/Button';
import { useAdmin } from './AdminContext';
import { useToast } from '../../contexts/ToastContext';
import { usersAPI, getApiBaseUrl } from '../../services/api';
import { AdminTable, ColumnDef, FilterDef } from './AdminTable';

const roleLabels: Record<string, string> = {
  superAdmin: 'Super Admin',
  admin: 'Admin',
  projectManager: 'Projectmanager',
  stakeholder: 'Stakeholder',
  viewer: 'Lezer',
};

export const UsersPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { users, loading, refreshData } = useAdmin();

  const handleDelete = async (userId: string) => {
    if (window.confirm('Weet je zeker dat je deze gebruiker wilt verwijderen?')) {
      try {
        await usersAPI.delete(userId);
        toast.success('Gebruiker verwijderd');
        refreshData();
      } catch (error: any) {
        toast.error('Verwijderen mislukt', error.response?.data?.error || 'Er ging iets mis.');
      }
    }
  };

  const handleBulkDelete = useCallback(async (ids: string[]) => {
    if (window.confirm(`Weet je zeker dat je ${ids.length} gebruiker(s) wilt verwijderen?`)) {
      try {
        await Promise.all(ids.map((id) => usersAPI.delete(id)));
        toast.success(`${ids.length} gebruiker(s) verwijderd`);
        refreshData();
      } catch (error: any) {
        toast.error('Verwijderen mislukt', error.response?.data?.error || 'Er ging iets mis.');
      }
    }
  }, [refreshData, toast]);

  const getUserName = (user: any): string => {
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    return user.name || '-';
  };

  const getAvatarUrl = (user: any): string | null => {
    if (!user.avatar) return null;
    if (user.avatar.startsWith('http')) return user.avatar;
    const origin = getApiBaseUrl().replace(/\/api$/, '');
    return `${origin}${user.avatar}`;
  };

  const getInitials = (user: any): string => {
    const first = user.firstName?.[0] || user.name?.[0] || '';
    const last = user.lastName?.[0] || '';
    return (first + last).toUpperCase() || 'U';
  };

  const columns: ColumnDef<any>[] = [
    {
      key: 'name',
      label: 'Naam',
      sortable: true,
      render: (user) => {
        const avatarUrl = getAvatarUrl(user);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
              background: '#E6007E', color: '#fff', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: '0.75rem', fontWeight: 600,
            }}>
              {avatarUrl
                ? <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : getInitials(user)
              }
            </div>
            <span style={{ fontWeight: 600 }}>{getUserName(user)}</span>
          </div>
        );
      },
      exportValue: (user) => getUserName(user),
    },
    {
      key: 'email',
      label: 'E-mail',
      sortable: true,
      render: (user) => user.email,
      exportValue: (user) => user.email || '',
    },
    {
      key: 'role',
      label: 'Rol',
      render: (user) => (
        <span className={`${styles.roleBadge} ${styles[`role${user.role}`]}`}>
          {roleLabels[user.role] || user.role}
        </span>
      ),
      exportValue: (user) => roleLabels[user.role] || user.role || '',
    },
    {
      key: 'projects',
      label: 'Projecten',
      render: (user) => {
        const count = user.projects?.length || 0;
        return (
          <span style={{ color: count > 0 ? '#323232' : '#9DA6A8' }}>
            {count}
          </span>
        );
      },
      exportValue: (user) => String(user.projects?.length || 0),
    },
    {
      key: 'status',
      label: 'Status',
      render: (user) => (
        <span className={`${styles.statusBadge} ${user.isActive !== false ? styles.statusActive : styles.statusInactive}`}>
          {user.isActive !== false ? 'Actief' : 'Inactief'}
        </span>
      ),
      exportValue: (user) => user.isActive !== false ? 'Actief' : 'Inactief',
    },
    {
      key: 'actions',
      label: 'Acties',
      render: (user) => (
        <div className={styles.actionButtons}>
          <button
            className={styles.editBtn}
            onClick={(e) => { e.stopPropagation(); navigate(`/admin/users/${user._id || user.id}/edit`); }}
          >
            Bewerk
          </button>
          <button
            className={styles.deleteBtn}
            onClick={(e) => { e.stopPropagation(); handleDelete(user._id || user.id); }}
          >
            Verwijder
          </button>
        </div>
      ),
    },
  ];

  const roles = Array.from(new Set(users.map((u: any) => u.role).filter(Boolean)));

  const filters: FilterDef[] = [
    {
      key: 'role',
      label: 'Alle rollen',
      options: roles.map((r: string) => ({ value: r, label: roleLabels[r] || r })),
    },
    {
      key: 'status',
      label: 'Alle statussen',
      options: [
        { value: 'active', label: 'Actief' },
        { value: 'inactive', label: 'Inactief' },
      ],
    },
  ];

  return (
    <>
      <AdminTable<any>
        data={users}
        columns={columns}
        filters={filters}
        filterFn={(user, activeFilters) => {
          if (activeFilters.role && user.role !== activeFilters.role) return false;
          if (activeFilters.status) {
            const isActive = user.isActive !== false;
            if (activeFilters.status === 'active' && !isActive) return false;
            if (activeFilters.status === 'inactive' && isActive) return false;
          }
          return true;
        }}
        searchFn={(user, query) =>
          getUserName(user).toLowerCase().includes(query) ||
          (user.email || '').toLowerCase().includes(query)
        }
        getId={(user) => user._id || user.id}
        onBulkDelete={handleBulkDelete}
        exportFilename="gebruikers"
        loading={loading}
        emptyIcon={
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" strokeWidth="1.5">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
          </svg>
        }
        emptyTitle="Nog geen gebruikers"
        emptyDescription="Voeg je eerste gebruiker toe om te beginnen."
        emptyAction={
          <Button onClick={() => navigate('/admin/users/new')}>
            + Nieuwe gebruiker
          </Button>
        }
        headerTitle="Gebruikers"
        headerAction={
          <Button onClick={() => navigate('/admin/users/new')}>
            + Nieuwe gebruiker
          </Button>
        }
      />

    </>
  );
};
